import { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiSearch, FiFileText, FiClock, FiExternalLink, FiAlertCircle, FiChevronDown, FiFilter } from 'react-icons/fi';
import { getInvoiceDocument, getAuditTrail } from '../../utils/ocrApi';

function fmtDateTime(ts) {
  if (!ts) return 'N/A';
  try { return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ts; }
}

function eventColor(type) {
  const t = (type || '').toUpperCase();
  if (t.includes('UPLOAD'))  return { bg: '#e3f2fd', color: '#0d47a1', dot: '#1565c0' };
  if (t.includes('REVIEW'))  return { bg: '#e8f5e9', color: '#1b5e20', dot: '#2e7d32' };
  if (t.includes('PUSH') || t.includes('SAP')) return { bg: '#fce4ec', color: '#880e4f', dot: '#ad1457' };
  if (t.includes('APPROV'))  return { bg: '#e8f5e9', color: '#1b5e20', dot: '#43a047' };
  if (t.includes('REJECT') || t.includes('BLOCK')) return { bg: '#ffebee', color: '#b71c1c', dot: '#c62828' };
  if (t.includes('EMAIL'))   return { bg: '#f3e5f5', color: '#4a148c', dot: '#7b1fa2' };
  return { bg: '#f5f7fa', color: '#3d5a80', dot: '#6b8299' };
}

// ── Document Preview Overlay ──────────────────────────────────────────────────
const DocumentPreviewOverlay = ({ invoiceNumber, url, expiresInMinutes, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="document-preview-overlay" onClick={onClose}>
      <div className="document-preview-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="document-preview-header">
          <h4>Document Preview{invoiceNumber ? ` — ${invoiceNumber}` : ''}</h4>
          <div className="document-preview-actions">
            <span className="document-preview-hint">Esc</span>
            <a href={url} target="_blank" rel="noopener noreferrer" className="document-preview-open-tab">
              <FiExternalLink size={13} /> Open in New Tab
            </a>
            <button className="document-preview-close" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="document-preview-meta">
          {expiresInMinutes
            ? `Secure link · expires in ${expiresInMinutes} minutes.`
            : 'Secure document link generated for preview.'}
        </div>
        <div className="document-preview-frame-wrap">
          <iframe src={url} title="Invoice document preview" className="document-preview-frame" />
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Audit Trail Overlay ───────────────────────────────────────────────────────
const AuditTrailOverlay = ({ invoiceNumber, data, loading, error, onClose }) => {
  const [filterType, setFilterType] = useState('ALL');

  const events = Array.isArray(data?.events)
    ? [...data.events].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
    : [];

  const types = ['ALL', ...new Set(events.map((e) => e.event_type).filter(Boolean))];
  const filtered = filterType === 'ALL' ? events : events.filter((e) => e.event_type === filterType);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="audit-trail-overlay" onClick={onClose}>
      <div className="audit-trail-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="audit-trail-header">
          <h4>Audit Trail{invoiceNumber ? ` — ${invoiceNumber}` : ''}</h4>
          <div className="audit-trail-actions">
            <span className="audit-shortcut-hint">Esc</span>
            <button className="audit-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {!loading && !error && data && (
          <div className="audit-trail-meta">
            <span>Invoice ID: {data.invoice_id || 'N/A'}</span>
            <span>Total Events: {data.total ?? events.length}</span>
          </div>
        )}

        {loading && <div className="audit-trail-empty">Loading audit trail…</div>}
        {!loading && error && <div className="audit-trail-empty error">{error}</div>}

        {!loading && !error && data && (
          <>
            {/* Event type filter chips */}
            {types.length > 2 && (
              <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <FiFilter size={13} style={{ color: 'var(--muted)', flexShrink: 0, marginTop: 4 }} />
                {types.map((t) => (
                  <button
                    key={t}
                    className={`inv-event-chip${filterType === t ? ' active' : ''}`}
                    onClick={() => setFilterType(t)}
                  >
                    {t} {t !== 'ALL' && `(${events.filter((e) => e.event_type === t).length})`}
                  </button>
                ))}
              </div>
            )}

            <div className="audit-trail-list">
              {filtered.length > 0 ? (
                <div className="inv-vtl">
                  {filtered.map((ev, i) => {
                    const clr = eventColor(ev.event_type);
                    return (
                      <div key={ev.id || `${ev.event_type}-${ev.timestamp}`} className="inv-vtl-item">
                        <div className="inv-vtl-left">
                          <div className="inv-vtl-dot" style={{ background: clr.bg, borderColor: clr.dot, color: clr.color }}>
                            {i + 1}
                          </div>
                          {i < filtered.length - 1 && <div className="inv-vtl-line" />}
                        </div>
                        <div className="inv-vtl-content" style={{ borderLeft: `3px solid ${clr.dot}` }}>
                          <div className="inv-vtl-header">
                            <span className="inv-vtl-type" style={{ color: clr.color }}>{ev.event_type || 'EVENT'}</span>
                            <span className="inv-vtl-time">{fmtDateTime(ev.timestamp)}</span>
                          </div>
                          <div className="inv-vtl-desc">{ev.description || 'No description.'}</div>
                          <div className="inv-vtl-actor">Actor: {ev.actor || 'Unknown'} · {ev.actor_type || 'N/A'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="audit-trail-empty">No audit events match the selected filter.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const OCRDocumentAudit = ({ uploadedInvoices = [] }) => {
  const [invoiceNum,   setInvoiceNum]   = useState('');
  const [searching,    setSearching]    = useState(false);
  const [searched,     setSearched]     = useState(false);

  const [docLoading,   setDocLoading]   = useState(false);
  const [docError,     setDocError]     = useState('');
  const [docData,      setDocData]      = useState(null);
  const [showDoc,      setShowDoc]      = useState(false);

  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError,   setAuditError]   = useState('');
  const [auditData,    setAuditData]    = useState(null);
  const [showAudit,    setShowAudit]    = useState(false);

  const fetchForInvoice = useCallback(async (num) => {
    if (!num) return;
    setInvoiceNum(num);
    setSearching(true); setSearched(false);
    setDocData(null);   setDocError('');
    setAuditData(null); setAuditError('');
    setShowDoc(false);  setShowAudit(false);
    setDocLoading(true); setAuditLoading(true);

    getInvoiceDocument(num)
      .then((d) => setDocData(d))
      .catch((e) => setDocError(e.message))
      .finally(() => setDocLoading(false));

    getAuditTrail(num)
      .then((d) => setAuditData(d))
      .catch((e) => setAuditError(e.message))
      .finally(() => { setAuditLoading(false); setSearching(false); setSearched(true); });
  }, []);

  useEffect(() => {
    if (uploadedInvoices.length > 0) fetchForInvoice(uploadedInvoices[0]);
  }, [uploadedInvoices, fetchForInvoice]);

  const handleSearch = () => fetchForInvoice(invoiceNum.trim());

  const docUrl    = docData?.document_url || docData?.url || docData?.file_url || null;
  const expiresIn = docData?.expires_in_minutes ?? null;

  const events   = Array.isArray(auditData?.events) ? auditData.events : [];
  const preview3 = [...events].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)).slice(0, 3);

  return (
    <div>
      {/* ── Search / Invoice selector ── */}
      <div className="inv-card" style={{ marginBottom: 20 }}>
        <div className="inv-card-title"><FiSearch size={16} /> Invoice Lookup</div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Dropdown of uploaded invoices */}
          {uploadedInvoices.length > 0 && (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <select
                value={invoiceNum}
                onChange={(e) => fetchForInvoice(e.target.value)}
                style={{
                  padding: '9px 32px 9px 12px', border: '1px solid var(--blue-3)',
                  borderRadius: 8, fontSize: '0.85rem', fontFamily: 'inherit',
                  color: 'var(--blue-1)', background: 'var(--blue-4)',
                  fontWeight: 600, cursor: 'pointer', outline: 'none',
                  appearance: 'none', minWidth: 200,
                }}
              >
                {uploadedInvoices.map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
              <FiChevronDown size={14} style={{ position: 'absolute', right: 10, color: 'var(--blue-1)', pointerEvents: 'none' }} />
            </div>
          )}

          {/* Manual search */}
          <div className="inv-filter-input" style={{ flex: 1, maxWidth: 320 }}>
            <FiSearch size={14} />
            <input
              placeholder="Or enter an invoice number…"
              value={uploadedInvoices.length > 0 ? '' : invoiceNum}
              onChange={(e) => setInvoiceNum(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <button
            className="inv-btn primary"
            onClick={handleSearch}
            disabled={!invoiceNum.trim() || searching}
          >
            {searching
              ? <><div className="inv-spinner" />Loading…</>
              : <><FiSearch size={14} />Search</>}
          </button>
        </div>

        {/* Uploaded invoice badge pills */}
        {uploadedInvoices.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
            <span style={{ fontSize: '0.74rem', color: 'var(--sub)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Uploaded:</span>
            {uploadedInvoices.map((num) => (
              <button
                key={num}
                onClick={() => fetchForInvoice(num)}
                style={{
                  padding: '3px 12px', borderRadius: 999, cursor: 'pointer',
                  border: `1.5px solid ${invoiceNum === num ? 'var(--blue-1)' : 'var(--blue-3)'}`,
                  background: invoiceNum === num ? 'linear-gradient(135deg,var(--blue-1),#1565c0)' : 'var(--blue-4)',
                  color: invoiceNum === num ? '#fff' : 'var(--blue-1)',
                  fontSize: '0.78rem', fontWeight: 700,
                  boxShadow: invoiceNum === num ? '0 2px 8px rgba(13,71,161,0.3)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {num}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Results ── */}
      {(searched || searching) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Document Card */}
          <div className="inv-card">
            <div className="inv-card-title"><FiFileText size={16} /> Document Preview</div>

            {docLoading && <div className="inv-loading"><div className="inv-spinner" /> Fetching document…</div>}
            {!docLoading && docError && <div className="inv-error-banner"><FiAlertCircle size={14} />{docError}</div>}

            {!docLoading && docData && (
              <>
                <div className="inv-info-strip" style={{ marginBottom: 16 }}>
                  {docData.invoice_number && <div className="inv-info-row"><span className="inv-info-label">Invoice #</span><span className="inv-info-value">{docData.invoice_number}</span></div>}
                  {docData.vendor_name    && <div className="inv-info-row"><span className="inv-info-label">Vendor</span><span className="inv-info-value">{docData.vendor_name}</span></div>}
                  {docData.invoice_date  && <div className="inv-info-row"><span className="inv-info-label">Date</span><span className="inv-info-value">{fmtDateTime(docData.invoice_date)}</span></div>}
                  {docData.status        && <div className="inv-info-row"><span className="inv-info-label">Status</span><span className="inv-info-value">{docData.status}</span></div>}
                  {expiresIn             && <div className="inv-info-row"><span className="inv-info-label">Link Expires</span><span className="inv-info-value" style={{ color: 'var(--orange)' }}>in {expiresIn} min</span></div>}
                </div>

                {docUrl ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="inv-btn primary" onClick={() => setShowDoc(true)}>
                      <FiFileText size={14} /> Preview Document
                    </button>
                    <a href={docUrl} target="_blank" rel="noopener noreferrer" className="inv-btn ghost" style={{ textDecoration: 'none' }}>
                      <FiExternalLink size={14} /> Open in New Tab
                    </a>
                  </div>
                ) : (
                  <div className="inv-empty" style={{ padding: 32 }}>
                    <div className="inv-empty-icon"><FiFileText size={24} /></div>
                    <p>No document URL returned for this invoice.</p>
                  </div>
                )}
              </>
            )}

            {!docLoading && !docData && !docError && (
              <div className="inv-empty" style={{ padding: 40 }}>
                <div className="inv-empty-icon"><FiFileText size={24} /></div>
                <p>No document found.</p>
              </div>
            )}
          </div>

          {/* Audit Card */}
          <div className="inv-card">
            <div className="inv-card-title"><FiClock size={16} /> Audit Trail</div>

            {auditLoading && <div className="inv-loading"><div className="inv-spinner" /> Loading audit events…</div>}
            {!auditLoading && auditError && <div className="inv-error-banner"><FiAlertCircle size={14} />{auditError}</div>}

            {!auditLoading && auditData && (
              <>
                {/* Summary row */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--sub)', fontWeight: 600 }}>
                    Invoice ID: {auditData.invoice_id || 'N/A'}
                  </span>
                  <span style={{
                    padding: '3px 10px', borderRadius: 999, background: 'var(--blue-4)',
                    border: '1px solid var(--blue-3)', fontSize: '0.74rem', fontWeight: 700, color: 'var(--blue-1)',
                  }}>
                    {auditData.total ?? events.length} events
                  </span>
                </div>

                {preview3.length > 0 ? (
                  <>
                    {/* Vertical timeline preview */}
                    <div className="inv-vtl" style={{ marginBottom: 12 }}>
                      {preview3.map((ev, i) => {
                        const clr = eventColor(ev.event_type);
                        return (
                          <div key={ev.id || i} className="inv-vtl-item">
                            <div className="inv-vtl-left">
                              <div className="inv-vtl-dot" style={{ background: clr.bg, borderColor: clr.dot, color: clr.color }}>
                                {i + 1}
                              </div>
                              {i < preview3.length - 1 && <div className="inv-vtl-line" />}
                            </div>
                            <div className="inv-vtl-content" style={{ borderLeft: `3px solid ${clr.dot}` }}>
                              <div className="inv-vtl-header">
                                <span className="inv-vtl-type" style={{ color: clr.color }}>{ev.event_type || 'EVENT'}</span>
                                <span className="inv-vtl-time">{fmtDateTime(ev.timestamp)}</span>
                              </div>
                              <div className="inv-vtl-desc">{ev.description || 'No description.'}</div>
                              <div className="inv-vtl-actor">{ev.actor || 'Unknown'} · {ev.actor_type || 'N/A'}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {events.length > 3 && (
                      <button className="inv-btn secondary" onClick={() => setShowAudit(true)}>
                        <FiClock size={14} /> View All {events.length} Events
                      </button>
                    )}
                    {events.length <= 3 && (
                      <button className="inv-btn secondary" onClick={() => setShowAudit(true)}>
                        <FiClock size={14} /> Full Audit Trail
                      </button>
                    )}
                  </>
                ) : (
                  <div className="inv-empty" style={{ padding: 32 }}>
                    <div className="inv-empty-icon"><FiClock size={22} /></div>
                    <p>No audit events available for this invoice.</p>
                  </div>
                )}
              </>
            )}

            {!auditLoading && !auditData && !auditError && (
              <div className="inv-empty" style={{ padding: 40 }}>
                <div className="inv-empty-icon"><FiClock size={22} /></div>
                <p>No audit trail found.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Initial empty state ── */}
      {!searched && !searching && uploadedInvoices.length === 0 && (
        <div className="inv-card">
          <div className="inv-empty" style={{ padding: 60 }}>
            <div className="inv-empty-icon"><FiSearch size={26} /></div>
            <strong>No Invoice Selected</strong>
            <p>Upload an invoice in the <strong>Invoice Processing</strong> tab, or enter an invoice number above to search manually.</p>
          </div>
        </div>
      )}

      {/* ── Overlays ── */}
      {showDoc && docUrl && (
        <DocumentPreviewOverlay
          invoiceNumber={invoiceNum} url={docUrl}
          expiresInMinutes={expiresIn} onClose={() => setShowDoc(false)}
        />
      )}
      {showAudit && (
        <AuditTrailOverlay
          invoiceNumber={invoiceNum} data={auditData}
          loading={auditLoading} error={auditError}
          onClose={() => setShowAudit(false)}
        />
      )}
    </div>
  );
};

export default OCRDocumentAudit;
